<!--
  ShopProgressiveImageUpload.vue
  
  Composant simplifié pour la prise de photos dans les shop deliveries
  basé sur ProgressiveImageUpload mais avec une interface plus légère
-->
<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import axios from '@/api/axios'
import localforage from 'localforage'

// Props
const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  deliveryId: {
    type: [Number, String],
    required: true
  },
  deliveryContext: {
    type: String,
    default: 'shop_photo',
    validator: (value) => ['shop_photo', 'shop_problem'].includes(value)
  },
  title: {
    type: String,
    default: 'Prendre une photo'
  },
  description: {
    type: String,
    default: 'Appuyez ici pour capturer une image'
  },
  required: {
    type: Boolean,
    default: false
  },
  preferredCamera: {
    type: String,
    default: 'environment', // 'environment' (arrière) ou 'user' (avant)
    validator: (value) => ['environment', 'user'].includes(value)
  },
  imageQuality: {
    type: Number, 
    default: 0.85
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'success', 'error', 'upload-progress', 'upload-complete', 'upload-error'])

// Store pour accéder au statut réseau
const store = useStore()
const isOnline = computed(() => store.getters['sync/isOnline'])

// État local
const image = ref(null)
const loading = ref(false)
const uploading = ref(false)
const uploadProgress = ref(0)
const error = ref(null)
const fileInput = ref(null)
const uploadedServerId = ref(null)
const localImageUrl = ref(null)
const isAndroid = ref(false)
const isIOS = ref(false)

// Détecter le type d'appareil
onMounted(() => {
  const ua = navigator.userAgent.toLowerCase()
  isAndroid.value = /android/.test(ua)
  isIOS.value = /iphone|ipad|ipod/.test(ua)
})

// Nettoyage à la destruction du composant
onUnmounted(() => {
  if (localImageUrl.value) {
    URL.revokeObjectURL(localImageUrl.value)
  }
  
  if (image.value?.thumbnailUrl) {
    URL.revokeObjectURL(image.value.thumbnailUrl)
  }
  
  if (image.value?.path && image.value.path.startsWith('blob:')) {
    URL.revokeObjectURL(image.value.path)
  }
})

// Observer les changements de valeur du modèle externe
watch(() => props.modelValue, (newValue) => {
  if (!newValue && image.value) {
    // Si la valeur externe est effacée, on nettoie l'état local aussi
    removeImage()
  } else if (newValue && !image.value) {
    // Si une valeur est définie à l'extérieur, on simule un chargement d'image
    image.value = {
      path: newValue,
      id: `external_${Date.now()}`
    }
  }
})

// Instance localforage pour les images temporaires (pendant l'upload)
const pendingImagesStore = localforage.createInstance({
  name: 'images',
  storeName: 'shop_pending_uploads'
})

// Méthode pour upload directement l'image au serveur
const uploadImageToServer = async (imageData) => {
  if (!isOnline.value) {
    // Si hors ligne, juste stocker localement
    return null
  }
  
  // Vérifier si l'image a déjà été synchronisée
  if (imageData.synced) {
    uploadedServerId.value = imageData.serverImageId
    return imageData.serverImageId
  }
  
  // Vérifier si on a déjà essayé trop de fois
  const MAX_RETRY = 3
  if (imageData.retryCount >= MAX_RETRY) {
    console.error(`Abandon de l'upload après ${MAX_RETRY} tentatives échouées`)
    error.value = `L'upload a échoué après plusieurs tentatives. Veuillez réessayer ultérieurement.`
    emit('upload-error', new Error(error.value))
    return null
  }
  
  // Préparer l'upload
  uploading.value = true
  uploadProgress.value = 0
  
  try {
    // Créer un controller pour pouvoir annuler la requête
    const abortController = new AbortController()
    
    // Créer le FormData pour l'upload
    const formData = new FormData()
    formData.append('image', imageData.file)
    formData.append('shop_delivery_id', props.deliveryId)
    formData.append('shop_delivery_context', props.deliveryContext)
    
    // Log de ce qui est envoyé pour debug
    console.log('Envoi de FormData pour shop delivery avec les clés:', [...formData.keys()])
    console.log('shop_delivery_id:', props.deliveryId, 'context:', props.deliveryContext)
    
    // Ajouter des métadonnées supplémentaires
    if (isAndroid.value || isIOS.value) {
      const deviceInfo = {
        platform: isAndroid.value ? 'android' : isIOS.value ? 'ios' : 'web',
        userAgent: navigator.userAgent
      }
      formData.append('device_info', JSON.stringify(deviceInfo))
    }
    
    // Configuration de la requête avec tracking de progression
    const config = {
      signal: abortController.signal,
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        uploadProgress.value = percentCompleted
        emit('upload-progress', percentCompleted)
      },
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 60000 // 60 secondes
    }
    
    // Envoyer l'image au serveur avec le bon endpoint pour shop deliveries
    const response = await axios.post('/livraison/shopDeliveryImage/', formData, config)
    
    // Récupérer l'ID côté serveur
    const serverId = response.data.id
    uploadedServerId.value = serverId
    
    // Marquer l'image comme synchronisée
    imageData.synced = true
    imageData.serverImageId = serverId
    imageData.syncedAt = new Date().toISOString()
    
    // Mettre à jour le compteur de tentatives
    imageData.retryCount = (imageData.retryCount || 0) + 1
    
    // Stocker la mise à jour dans le pendingImagesStore
    await pendingImagesStore.setItem(imageData.id, imageData)
    
    // Émettre l'événement de succès avec l'ID serveur
    emit('upload-complete', serverId, imageData)
    
    console.log('Shop delivery image uploadée avec succès, ID serveur:', serverId)
    
    return serverId
  } catch (err) {
    console.error('Erreur lors de l\'upload de l\'image shop delivery:', err)
    
    // Déterminer le type d'erreur
    let errorMessage = 'Erreur lors de l\'upload de l\'image'
    
    if (err.code === 'ECONNABORTED') {
      errorMessage = 'La connexion a expiré. Votre réseau est peut-être lent.'
    } else if (err.response) {
      // Erreur de réponse du serveur
      if (err.response.status >= 500) {
        errorMessage = 'Erreur serveur. Veuillez réessayer ultérieurement.'
      } else if (err.response.status === 413) {
        errorMessage = 'Image trop volumineuse pour le serveur.'
      } else if (err.response.status === 401 || err.response.status === 403) {
        errorMessage = 'Accès non autorisé. Veuillez vous reconnecter.'
      } else {
        errorMessage = `Erreur ${err.response.status}: ${err.response.data?.message || 'Erreur inconnue'}`
      }
    } else if (err.request) {
      // Erreur de réseau
      errorMessage = 'Impossible de joindre le serveur. Vérifiez votre connexion.'
    }
    
    error.value = errorMessage
    
    // Mettre à jour le compteur de tentatives
    imageData.retryCount = (imageData.retryCount || 0) + 1
    imageData.lastError = {
      message: errorMessage,
      timestamp: new Date().toISOString()
    }
    
    // Stocker la mise à jour dans le pendingImagesStore
    await pendingImagesStore.setItem(imageData.id, imageData)
    
    emit('upload-error', err)
    
    return null
  } finally {
    uploading.value = false
  }
}

// Obtenir l'URL d'une image
const getImageUrl = (path) => {
  if (!path) return ''
  if (path.startsWith('blob:') || path.startsWith('data:')) return path
  if (path.startsWith('http')) return path
  return `${import.meta.env.VITE_APP_BASE_URL}${path}`
}

// Méthode pour prendre une photo
const takePicture = () => {
  // Empêcher les actions pendant le chargement
  if (loading.value || uploading.value) return
  
  // Cliquer sur l'input pour ouvrir l'appareil photo
  if (fileInput.value) {
    fileInput.value.click()
  }
}

// Ajouter un timestamp sur l'image
const formatTimestamp = () => {
  const now = new Date()
  const date = now.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  const time = now.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
  return `${date} ${time}`
}

// Compresser l'image et ajouter un horodatage
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)

    reader.onload = (event) => {
      const img = new Image()
      img.onload = () => {
        // Créer un canvas pour la manipulation d'image
        const canvas = document.createElement('canvas')
        // Déterminer les dimensions
        let width = img.width
        let height = img.height

        // Ajuster les dimensions si nécessaire
        const MAX_WIDTH = 1280
        const MAX_HEIGHT = 1280

        // Déterminer le facteur de redimensionnement
        let scaleFactor = 1;
        
        if (width > height) {
          if (width > MAX_WIDTH) {
            scaleFactor = MAX_WIDTH / width;
          }
        } else {
          if (height > MAX_HEIGHT) {
            scaleFactor = MAX_HEIGHT / height;
          }
        }
        
        width = Math.floor(width * scaleFactor);
        height = Math.floor(height * scaleFactor);

        // Définir les dimensions du canvas
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        
        // Dessiner l'image
        ctx.drawImage(img, 0, 0, width, height)

        // Ajouter le timestamp
        const timestamp = formatTimestamp()
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
        ctx.font = '16px Arial'
        const textMetrics = ctx.measureText(timestamp)
        const textWidth = textMetrics.width
        const padding = 8
        const textHeight = 20

        ctx.fillRect(
          canvas.width - textWidth - padding * 2,
          canvas.height - textHeight - padding * 2,
          textWidth + padding * 2,
          textHeight + padding * 2
        )

        ctx.fillStyle = 'rgba(0, 0, 0, 0.9)'
        ctx.fillText(
          timestamp,
          canvas.width - textWidth - padding,
          canvas.height - padding - 6
        )

        // Générer le blob avec la qualité configurée
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Échec de la génération de l\'image'))
            return
          }
          
          resolve(new File([blob], file.name || 'image.jpg', {
            type: 'image/jpeg',
            lastModified: Date.now()
          }))
        }, 'image/jpeg', props.imageQuality)
      }

      img.onerror = () => {
        reject(new Error('Échec du chargement de l\'image'))
      }
      
      img.src = event.target.result
    }

    reader.onerror = () => {
      reject(new Error('Échec de la lecture du fichier'))
    }
  })
}

// Créer une miniature pour l'affichage
const createThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    
    reader.onload = (event) => {
      const img = new Image()
      
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        // Dimensions pour la miniature
        const thumbnailWidth = 300
        const thumbnailHeight = 300
        
        // Calculer le ratio pour conserver les proportions
        const ratio = Math.min(thumbnailWidth / img.width, thumbnailHeight / img.height)
        const width = img.width * ratio
        const height = img.height * ratio
        
        // Centrer l'image dans le canvas
        canvas.width = thumbnailWidth
        canvas.height = thumbnailHeight
        
        // Remplir avec du gris léger
        ctx.fillStyle = '#f1f1f1'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        
        // Dessiner l'image centrée
        const x = (canvas.width - width) / 2
        const y = (canvas.height - height) / 2
        
        ctx.drawImage(img, x, y, width, height)
        
        // Convertir en URL de données
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Échec de génération de la miniature'))
            return
          }
          resolve(URL.createObjectURL(blob))
        }, 'image/jpeg', 0.7) // Qualité réduite pour la miniature
      }
      
      img.onerror = () => {
        reject(new Error('Échec du chargement pour la miniature'))
      }
      
      img.src = event.target.result
    }
    
    reader.onerror = () => {
      reject(new Error('Échec de lecture pour la miniature'))
    }
  })
}

// Traiter un fichier image
const processFile = async (file) => {
  try {
    loading.value = true;
    
    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      throw new Error('Type de fichier non supporté. Veuillez sélectionner une image.');
    }
    
    // Limiter la taille du fichier (20MB)
    const MAX_FILE_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('L\'image est trop volumineuse. La taille maximale est de 20MB.');
    }
    
    // Essayer de compresser l'image
    let compressedFile;
    try {
      compressedFile = await compressImage(file);
    } catch (compressionError) {
      console.error('Erreur lors de la compression:', compressionError);
      // En cas d'échec de compression, utiliser le fichier original
      compressedFile = file;
    }
    
    // Créer une miniature pour l'affichage
    let thumbnailUrl;
    try {
      thumbnailUrl = await createThumbnail(compressedFile);
    } catch (thumbnailError) {
      console.error('Erreur lors de la création de la miniature:', thumbnailError);
      // Utiliser l'image compressée comme miniature en cas d'échec
      thumbnailUrl = URL.createObjectURL(compressedFile);
    }
    
    // Créer l'objet image avec toutes les données nécessaires
    const imageData = {
      id: `shop_img_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
      file: compressedFile,
      path: URL.createObjectURL(compressedFile),
      thumbnailUrl: thumbnailUrl,
      deliveryId: props.deliveryId,
      deliveryContext: props.deliveryContext,
      // S'assurer que ces attributs sont correctement identifiés comme une image shop_delivery
      type: 'shop_delivery',
      fileType: file.type || 'image/jpeg',
      fileName: file.name || `shop_image_${Date.now()}.jpg`,
      size: compressedFile.size,
      synced: false,
      retryCount: 0,
      initialFileSize: file.size,
      compressedFileSize: compressedFile.size,
      timestamp: new Date().toISOString()
    };
    
    // Stocker l'image dans l'état local avant de la convertir en base64
    image.value = imageData;
    
    // Stocker l'image temporairement dans le stockage local
    await pendingImagesStore.setItem(imageData.id, imageData);
    
    // Utiliser le service de synchronisation pour gérer le stockage AVANT de convertir en base64
    let imageId = null;
    try {
      // Importer dynamiquement le service de synchronisation
      const { syncService } = await import('@/services/syncService');
      
      // Stocker l'image avec le service pour une synchronisation ultérieure
      const metadata = {
        id: imageData.id,
        deliveryId: props.deliveryId,
        deliveryContext: props.deliveryContext,
        type: 'shop_delivery', // Identifier explicitement comme une image shop_delivery
        fileName: imageData.fileName,
        fileType: imageData.fileType,
        size: imageData.size
      };
      
      // Log détaillé pour le débogage
      console.log('Stockage d\'une image shop_delivery pour synchronisation ultérieure:', {
        id: imageData.id,
        type: 'shop_delivery',
        context: props.deliveryContext,
        deliveryId: props.deliveryId,
        fileType: typeof compressedFile
      });
      
      // Stocker l'image avant d'émettre le v-model
      imageId = await syncService.storePendingImage(compressedFile, metadata);
      
      console.log('Image shop_delivery stockée pour synchronisation ultérieure:', imageId);
    } catch (storageError) {
      console.error('Erreur lors du stockage de l\'image pour synchronisation:', storageError);
      // Continuer malgré l'erreur - l'image est déjà dans pendingImagesStore
    }
    
    // Émettre directement l'ID de l'image, sans conversion base64 supplémentaire
    // Ceci évite que l'image soit dupliquée en mode hors-ligne
    emit('update:modelValue', imageId || imageData.id);
    
    // Éviter de créer des instances FileReader inutiles qui pourraient causer des problèmes
    // La conversion en base64 n'est plus nécessaire puisqu'on stocke directement l'ID
    
    // Émettre l'événement de succès de prise de photo
    emit('success', imageData.id, imageData);
    
    // Si on est en ligne, commencer l'upload immédiatement
    if (isOnline.value) {
      try {
        await uploadImageToServer(imageData);
      } catch (uploadError) {
        console.error('Erreur initiale d\'upload de la shop delivery image:', uploadError);
        // L'erreur est déjà gérée dans uploadImageToServer
      }
    }
    
    return imageData;
  } catch (err) {
    console.error('Erreur lors du traitement de l\'image:', err);
    error.value = err.message || 'Échec du traitement de l\'image';
    emit('error', error.value);
    return null;
  } finally {
    loading.value = false;
    if (fileInput.value) {
      fileInput.value.value = ''; // Réinitialiser l'input pour permettre de prendre la même photo
    }
  }
}

// Gérer la sélection de fichiers
const handleFiles = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  
  if (!file.type.startsWith('image/')) {
    error.value = 'Seules les images sont autorisées'
    return
  }
  
  await processFile(file)
}

// Supprimer l'image
const removeImage = () => {
  // Libérer l'URL de l'image
  if (localImageUrl.value) {
    URL.revokeObjectURL(localImageUrl.value)
    localImageUrl.value = null
  }
  
  // Libérer l'URL de la miniature
  if (image.value?.thumbnailUrl) {
    URL.revokeObjectURL(image.value.thumbnailUrl)
  }
  
  // Libérer l'URL de l'image originale
  if (image.value?.path && image.value.path.startsWith('blob:')) {
    URL.revokeObjectURL(image.value.path)
  }
  
  // Réinitialiser l'état
  image.value = null
  uploadedServerId.value = null
  uploadProgress.value = 0
  error.value = null
  
  // Émettre la valeur vide
  emit('update:modelValue', '')
}

// Exposer les propriétés et méthodes nécessaires pour le composant parent
defineExpose({
  image,
  takePicture,
  removeImage
})
</script>

<template>
  <div class="shop-uploader-wrapper">
    <div class="uploader-section">
      <h6 class="uploader-title">{{ title }}</h6>
      <small v-if="description" class="uploader-description">{{ description }}</small>
      <small v-if="required" class="required-indicator">* Obligatoire</small>
    </div>
    
    <div 
      class="uploader-container" 
      :class="{ 
        'has-image': image, 
        'is-uploading': uploading, 
        'is-ios': isIOS,
        'is-android': isAndroid,
        'is-required': required
      }" 
      @click="takePicture"
    >
      <!-- Input file pour la capture de photos -->
      <input 
        type="file" 
        ref="fileInput" 
        accept="image/*" 
        capture="environment"
        @change="handleFiles"
        style="display: none" 
      />

      <!-- État vide - Pas d'image -->
      <div v-if="!image && !loading" class="empty-state">
        <i class="pi pi-camera camera-icon"></i>
        <span class="camera-text">{{ required ? 'Prendre une photo*' : 'Prendre une photo' }}</span>
      </div>

      <!-- État de chargement - Traitement de l'image -->
      <div v-if="loading" class="loading-container">
        <i class="pi pi-spin pi-spinner"></i>
        <span class="loading-text">Traitement...</span>
      </div>

      <!-- Aperçu de l'image -->
      <div v-if="image && !loading" class="image-preview">
        <img 
          :src="image.thumbnailUrl || getImageUrl(image.path)" 
          class="preview-image" 
          :alt="`Image ${image.id}`" 
        />
        
        <!-- Bouton de suppression -->
        <button 
          type="button" 
          @click.stop="removeImage" 
          class="remove-button"
          aria-label="Supprimer l'image"
        >
          <i class="pi pi-times"></i>
        </button>
        
        <!-- Barre de progression pour l'upload -->
        <div v-if="uploading" class="upload-progress-container">
          <div class="upload-progress-bar" :style="{ width: `${uploadProgress}%` }"></div>
          <span class="upload-progress-text">{{ uploadProgress }}%</span>
        </div>
        
        <!-- Indicateur d'état selon si l'image est uploadée ou en attente -->
        <div 
          v-if="!uploading && image" 
          class="status-indicator" 
          :class="{ 'success': uploadedServerId, 'pending': !uploadedServerId }"
        >
          <i :class="uploadedServerId ? 'pi pi-check' : 'pi pi-clock'"></i>
          <span class="indicator-text">
            {{ uploadedServerId ? 'Synchronisée' : (isOnline ? 'En attente' : 'Hors ligne') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Message d'erreur -->
    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<style scoped>
.shop-uploader-wrapper {
  width: 100%;
  margin-bottom: 1rem;
}

.uploader-section {
  margin-bottom: 0.5rem;
}

.uploader-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.25rem 0;
}

.uploader-description {
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.required-indicator {
  color: #ef4444;
  font-size: 0.8rem;
}

.uploader-container {
  background-color: #f9fafb;
  border: 2px dashed #e5e7eb;
  border-radius: 0.5rem;
  height: 120px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.uploader-container:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.uploader-container.is-required {
  border-color: #3b82f6;
}

.empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
}

.camera-icon {
  font-size: 2rem;
  color: #9ca3af;
}

.camera-text {
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
}

.loading-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.loading-container .pi-spinner {
  font-size: 2rem;
  color: #3b82f6;
}

.loading-text {
  font-size: 0.875rem;
  color: #3b82f6;
}

.image-preview {
  width: 100%;
  height: 100%;
  position: relative;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.remove-button {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: rgba(239, 68, 68, 0.8);
  color: white;
  border: none;
  border-radius: 50%;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.remove-button:hover {
  background-color: rgba(220, 38, 38, 1);
}

/* Barre de progression */
.upload-progress-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 24px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1;
  padding: 0;
  display: flex;
  align-items: center;
}

.upload-progress-bar {
  height: 100%;
  background-color: #3b82f6;
  transition: width 0.2s ease;
}

.upload-progress-text {
  position: absolute;
  width: 100%;
  text-align: center;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Indicateur de statut */
.status-indicator {
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
}

.status-indicator.success {
  background-color: rgba(46, 204, 113, 0.8);
}

.status-indicator.pending {
  background-color: rgba(243, 156, 18, 0.8);
}

.error-message {
  margin-top: 0.5rem;
  color: #ef4444;
  font-size: 0.75rem;
}

/* Adaptations pour les appareils mobiles */
@media (max-width: 640px) {
  .uploader-container {
    height: 100px;
  }
  
  .camera-icon {
    font-size: 1.75rem;
  }
}
</style> 